import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/useToast";
import { supabase } from "@/lib/supabase";
import useAuthStore from "@/store/authStore";
import { Lightbulb, X } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import ColorIconChooser from "@/components/ui/color-icon-chooser";

export default function EditProjectDialog({
  isOpen,
  onOpenChange,
  workspace,
  onSave,
}) {
  const { user, workspaces, setWorkspaces } = useAuthStore();
  const { toast } = useToast();

  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showColorChooser, setShowColorChooser] = useState(false);

  // Character limits
  const NAME_LIMIT = 100;
  const DESCRIPTION_LIMIT = 500;

  // Initialize form with workspace data
  useEffect(() => {
    if (workspace && isOpen) {
      setProjectName(workspace.name || "");
      setProjectDescription(workspace.description || "");
    }
  }, [workspace, isOpen]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setShowColorChooser(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!projectName.trim()) {
      toast({
        title: "Project name required",
        description: "Please enter a name for your project.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const updateData = {
        name: projectName.trim(),
        description: projectDescription.trim(),
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("workspaces")
        .update(updateData)
        .eq("id", workspace.id);

      if (error) throw error;

      // Update local state
      const updatedWorkspaces = workspaces.map((ws) =>
        ws.id === workspace.id ? { ...ws, ...updateData } : ws
      );
      setWorkspaces(updatedWorkspaces);

      toast({
        title: "Project updated",
        description: "Your project settings have been saved.",
      });

      onSave?.({ ...workspace, ...updateData });
      setShowColorChooser(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating project:", error);
      toast({
        title: "Error updating project",
        description: error.message || "Failed to save project settings.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorSelect = async (color) => {
    try {
      const updatedMetadata = {
        ...(workspace.metadata || {}),
        color: color.value,
      };

      const { error } = await supabase
        .from("workspaces")
        .update({
          metadata: updatedMetadata,
          updated_at: new Date().toISOString(),
        })
        .eq("id", workspace.id);

      if (error) throw error;

      // Update local state
      const updatedWorkspaces = workspaces.map((ws) =>
        ws.id === workspace.id ? { ...ws, metadata: updatedMetadata } : ws
      );
      setWorkspaces(updatedWorkspaces);
    } catch (error) {
      console.error("Error updating color:", error);
      toast({
        title: "Error updating color",
        description: "Failed to update project color.",
        variant: "destructive",
      });
    }
  };

  const handleIconSelect = async (icon) => {
    try {
      const updatedMetadata = {
        ...(workspace.metadata || {}),
        icon: icon.value,
      };

      const { error } = await supabase
        .from("workspaces")
        .update({
          metadata: updatedMetadata,
          updated_at: new Date().toISOString(),
        })
        .eq("id", workspace.id);

      if (error) throw error;

      // Update local state
      const updatedWorkspaces = workspaces.map((ws) =>
        ws.id === workspace.id ? { ...ws, metadata: updatedMetadata } : ws
      );
      setWorkspaces(updatedWorkspaces);
    } catch (error) {
      console.error("Error updating icon:", error);
      toast({
        title: "Error updating icon",
        description: "Failed to update project icon.",
        variant: "destructive",
      });
    }
  };

  const getWorkspaceIcon = () => {
    const icon = workspace?.metadata?.icon;
    const color = workspace?.metadata?.color;

    if (icon?.startsWith("http")) {
      return (
        <Avatar className="h-12 w-12 cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all">
          <AvatarImage src={icon} alt={workspace.name} />
          <AvatarFallback
            className="text-lg font-semibold"
            style={{ backgroundColor: color || "#9333ea" }}
          >
            {workspace.name?.charAt(0)?.toUpperCase() || "P"}
          </AvatarFallback>
        </Avatar>
      );
    }

    return (
      <div
        className="h-12 w-12 rounded-full flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all text-2xl"
        style={{ backgroundColor: color || "#9333ea" }}
      >
        {icon || workspace?.name?.charAt(0)?.toUpperCase() || "P"}
      </div>
    );
  };

  // Handle dialog close - ensure popover is closed first
  const handleOpenChange = (open) => {
    if (!open) {
      setShowColorChooser(false);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={true}>
      <DialogContent
        className="max-w-[90vw] w-[500px] bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-800 rounded-2xl"
        style={{ maxHeight: "min(90vh, 600px)" }}
        hideCloseButton={true}
        onPointerDownOutside={(e) => {
          // Prevent closing if clicking on the ColorIconChooser popover
          if (showColorChooser) {
            e.preventDefault();
          }
        }}
        onCloseAutoFocus={(e) => {
          // Prevent focus from going to a non-existent trigger element
          e.preventDefault();
          // Blur any focused element to release the focus trap
          setTimeout(() => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }, 0);
        }}
      >
          <DialogHeader className="relative">
            <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
              Edit Project
            </DialogTitle>
            <DialogDescription className="sr-only">
              Edit your project name, description, and settings
            </DialogDescription>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleOpenChange(false)}
              className="absolute right-0 top-0 h-8 w-8 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Project Name with Icon */}
            <div className="space-y-2">
              <Label
                htmlFor="project-name"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Project Name
              </Label>
              <div className="flex items-center gap-3 p-3 border border-gray-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800">
                <ColorIconChooser
                  isOpen={showColorChooser}
                  onOpenChange={setShowColorChooser}
                  currentColor={workspace?.metadata?.color || "#9333ea"}
                  currentIcon={workspace?.metadata?.icon || "folder"}
                  onColorSelect={handleColorSelect}
                  onIconSelect={handleIconSelect}
                >
                  <button
                    type="button"
                    onClick={() => setShowColorChooser(true)}
                    className="focus:outline-none"
                  >
                    {getWorkspaceIcon()}
                  </button>
                </ColorIconChooser>
                <Input
                  id="project-name"
                  value={projectName}
                  onChange={(e) =>
                    setProjectName(e.target.value.slice(0, NAME_LIMIT))
                  }
                  placeholder="Enter project name"
                  className="flex-1 border-0 bg-transparent focus-visible:ring-0 p-0 h-auto text-base"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
                {projectName.length}/{NAME_LIMIT}
              </p>
            </div>

            {/* Project Description */}
            <div className="space-y-2">
              <Label
                htmlFor="project-description"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Description{" "}
                <span className="text-gray-400 dark:text-gray-500">
                  (optional)
                </span>
              </Label>
              <Textarea
                id="project-description"
                value={projectDescription}
                onChange={(e) =>
                  setProjectDescription(
                    e.target.value.slice(0, DESCRIPTION_LIMIT)
                  )
                }
                placeholder="Describe your project..."
                className="min-h-[80px] resize-none border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
                {projectDescription.length}/{DESCRIPTION_LIMIT}
              </p>
            </div>

            {/* Helper Text */}
            <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-zinc-800/50 rounded-lg">
              <Lightbulb className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Editing your project
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Click the icon to upload a custom image for your team project.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !projectName.trim()}
              className="bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100 rounded-2xl"
            >
              {isSaving ? (
                <>
                  <Spinner className="h-4 w-4 mr-2" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}

EditProjectDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  workspace: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    metadata: PropTypes.shape({
      icon: PropTypes.string,
      color: PropTypes.string,
    }),
  }),
  onSave: PropTypes.func,
};
